import { NgTemplateOutlet } from "@angular/common";
import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  ContentChild,
  ElementRef,
  Input,
  OnDestroy,
  OnInit,
  TemplateRef,
  ViewChild,
  computed,
  effect,
  inject,
  input,
  model,
  signal,
  viewChildren,
} from "@angular/core";
import { takeUntilDestroyed } from "@angular/core/rxjs-interop";
import { NavigationEnd, Router } from "@angular/router";
import { faXmark } from "@fortawesome/free-solid-svg-icons/faXmark";
import { Subject, debounceTime, filter, skip } from "rxjs";
import { MAX_SAFE_INTEGER } from "src/config";
import { FocusTrapDirective } from "src/prime/focustrap/focustrap";
import { KeyboardShortcutService } from "src/standard/services/keyboard-shortcut.service";
import { ScrollbarWidthService } from "src/standard/services/scrollbar-width.service";
import { StandardButtonComponent } from "src/standard/standard-button/standard-button.component";
import { WindowManagerService } from "src/standard/standard-modal-window/window-manager.service";
import { StandardNavLinkDropdownVariants } from "src/standard/standard-nav-link-dropdown/variants";
import {
  getWidthCategory,
  LG,
  MD,
  SM,
  WidthCategory,
  XL,
  XS,
} from "src/standard/standard-nav-shell/standard-nav-shell.types";
import {
  StandardNavSideActionsContext,
  StandardNavSideComponent,
} from "src/standard/standard-nav-side/standard-nav-side.component";
import {
  StandardNavTopActionsContext,
  StandardNavTopComponent,
} from "src/standard/standard-nav-top/standard-nav-top.component";
import { StandardResizeSideComponent } from "src/standard/standard-resize-side/standard-resize-side.component";
import { StandardSkipNavContentComponent } from "src/standard/standard-skip-nav/standard-skip-nav-content.component";
import { StandardSkipNavLinkComponent } from "src/standard/standard-skip-nav/standard-skip-nav-link.component";
import { StandardTooltip } from "src/standard/standard-tooltip/standard-tooltip.component";
import { convertTo } from "src/standard/ts/convertTo";
import { ColorMode, KeysOfType } from "src/standard/ts/types";
import invariant from "tiny-invariant";

export * from "./standard-nav-shell.types";

type TemplateSlot<T extends string> = Extract<
  KeysOfType<StandardNavShellComponent, TemplateRef<never> | null>,
  `${T}${string}`
>;
export type NavTemplatesMap = Record<
  Extract<Omit<TemplateSlot<"nav">, "navTopTemplate">, string>,
  TemplateRef<never> | null
>;

interface BaseNavContext {
  hasNavSide: boolean | undefined;
  navSideExpanded: boolean | undefined;
  navSideMinimized: boolean | undefined;
  toggleNavSide: () => void;
  colorMode: ColorMode | undefined;
}

export type NavTopLogoContext = BaseNavContext;
export type NavSideLogoContext = Omit<BaseNavContext, "navSideMinimized"> & {
  minimized: boolean | undefined;
};
export type NavSideActionsContext = Omit<BaseNavContext, "navSideMinimized"> &
  StandardNavSideActionsContext & {
    minimized: boolean | undefined;
  };
export type NavTopActionsContext = BaseNavContext &
  StandardNavTopActionsContext;

@Component({
  standalone: true,
  selector: "standard-nav-shell",
  templateUrl: "./standard-nav-shell.component.html",
  styleUrl: "./standard-nav-shell.component.scss",
  imports: [
    NgTemplateOutlet,

    FocusTrapDirective,

    StandardNavTopComponent,
    StandardNavSideComponent,

    StandardTooltip,
    StandardButtonComponent,
    StandardSkipNavLinkComponent,
    StandardSkipNavContentComponent,

    StandardResizeSideComponent,
    StandardResizeSideComponent,
  ],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class StandardNavShellComponent implements OnInit, OnDestroy {
  XS = XS;
  SM = SM;
  MD = MD;
  LG = LG;
  XL = XL;
  StandardNavLinkDropdownVariants = StandardNavLinkDropdownVariants;
  faXmark = faXmark;

  private host = inject<ElementRef<HTMLElement>>(ElementRef);
  private router = inject(Router);
  private windowManagerService = inject(WindowManagerService);
  private changeDetectorRef = inject(ChangeDetectorRef);
  private readonly keyboardShortcutService = inject(KeyboardShortcutService);
  private readonly scrollbarWidthService = inject(ScrollbarWidthService);

  private updateWidthCategoryTrigger$ = new Subject<void>();
  public hostEl: HTMLElement;

  constructor() {
    this.hostEl = this.host.nativeElement;

    // Auto-dismiss side nav when navigating (skip initial page load)
    this.router.events
      .pipe(
        filter((event) => event instanceof NavigationEnd),
        skip(1),
        takeUntilDestroyed(),
      )
      .subscribe(() => {
        if (this.isOverlayOpen()) {
          this.toggleNavSide(false);
        }

        this.resetScroll("smooth");
      });

    this.keyboardShortcutService.register(
      [
        [
          "Escape",
          (e) => {
            e.preventDefault();

            this.toggleNavSide(false);
          },
        ],
      ],
      this.isOverlayOpen,
    );

    // When the update thing is triggered,
    // debounce it to prevent multiple updates
    this.updateWidthCategoryTrigger$
      .pipe(debounceTime(10), takeUntilDestroyed())
      .subscribe(() => this.updateWidthCategory());

    this.registerNavTemplates = this.registerNavTemplates.bind(this);
    this.onToggleNavSide = this.onToggleNavSide.bind(this);
  }

  readonly isNavSideInert = input<boolean>();
  readonly isContentInert = input<boolean>();

  @Input() scrollActiveNavItemIntoView: boolean = false;
  readonly collapseToMinimized = input<boolean>(false);

  @Input() topColorMode: ColorMode | undefined;
  @Input() topStyleClass: string | undefined;
  readonly sideEdge = input<"start" | "end">("start");
  @Input() sideColorMode: ColorMode | undefined;
  @Input() bottomColorMode: ColorMode | undefined;

  @Input() navSideHeaderStyleClass: string | undefined;
  @Input() navSidePreStartActionsStyleClass: string | undefined;
  @Input() navSideStartActionsStyleClass: string | undefined;
  @Input() navSideEndActionsStyleClass: string | undefined;

  @Input() topStartToSideStartMaxSize: number = MD;
  @Input() topEndToSideEndMaxSize: number = SM;
  @Input() sideOverlayFullMinSize: number = 0;
  @Input() sideOverlayFullMaxSize: number = MD;
  readonly sideOverlayExpandedFullMaxSize = input<number>(XS);
  @Input() defaultSideCollapsedMinSize: number = 0;
  @Input() defaultSideCollapsedMaxSize: number = MD;
  @Input() autoHideSideNavCutoffMaxSize: number = MD;

  @Input() autoShow: boolean = true;
  @Input() autoShowSideNavCutoffMinSize: number = XL;
  @Input() autoShowSideNavCutoffMaxSize: number = MAX_SAFE_INTEGER;

  readonly scrollContent = input<boolean>(false);
  readonly showClose = input<boolean>();

  readonly navSideResizable = input<boolean>(false);
  readonly navSideResizableMin = input<number>(0);
  readonly navSideResizableMax = input<number>(MAX_SAFE_INTEGER);

  public readonly preferredNavSideExpandedKey = model<string>();
  public readonly preferredNavSideExpanded = model<boolean>();

  public readonly navSideExpanded = model<boolean>();
  public readonly navSideOverlayed = model<boolean | undefined>(undefined);
  public readonly isOverlayOpen = computed(
    () => this.navSideOverlayed() && this.navSideExpanded(),
  );

  protected readonly navSideVisibilityState = computed(() => {
    return this.navSideOverlayed()
      ? this.collapseToMinimized()
        ? "minimized"
        : "collapsed"
      : this.navSideExpanded()
        ? "expanded"
        : this.collapseToMinimized()
          ? "minimized"
          : "collapsed";
  });
  protected readonly navSideOverlayBgVisibilityState = computed(() => {
    return !this.navSideOverlayed()
      ? "collapsed"
      : this.navSideExpanded()
        ? "expanded"
        : "collapsed";
  });
  protected readonly navSideOverlayContentVisibilityState = computed(() => {
    const widthCategory = this.widthCategory();
    return !this.navSideOverlayed()
      ? "collapsed"
      : this.navSideExpanded()
        ? widthCategory !== undefined &&
          widthCategory <= this.sideOverlayExpandedFullMaxSize()
          ? "expandedFull"
          : "expanded"
        : "collapsed";
  });

  // Signals to control when overlay elements should be rendered in DOM
  // (considering animation timing)
  protected readonly shouldRenderNavSides = signal<boolean>(false);
  protected readonly allowNavSideAnimationIn = signal<boolean>(false);

  // Effect to manage overlay DOM rendering with animation timing
  private readonly manageOverlayRendering = effect((cleanup) => {
    const overlayed = this.navSideOverlayed();
    const expanded = this.navSideExpanded();

    if (overlayed && expanded) {
      // Add to DOM immediately in collapsed state
      this.shouldRenderNavSides.set(true);
      this.allowNavSideAnimationIn.set(false);

      // Then allow animation in next frame
      const animationTimer = requestAnimationFrame(() => {
        this.allowNavSideAnimationIn.set(true);
      });

      cleanup(() => {
        cancelAnimationFrame(animationTimer);
      });
    } else if (overlayed && !expanded) {
      // Start animation out immediately
      this.allowNavSideAnimationIn.set(false);

      // Remove from DOM after animation completes
      const timer = setTimeout(() => {
        this.shouldRenderNavSides.set(false);
      }, 200); // animation completes in 200ms

      cleanup(() => {
        clearTimeout(timer);
      });
    } else {
      // Not overlayed, remove immediately
      this.shouldRenderNavSides.set(false);
      this.allowNavSideAnimationIn.set(false);
    }
  });

  //
  // After reflowing, we want to update side panel positions
  //
  protected readonly animate = signal<boolean>(false);
  private readonly syncNavSideAnimated = effect((cleanup) => {
    const hasNavSide = this.hasNavSide();
    if (!hasNavSide) {
      return;
    }

    // trigger on change
    this.navSideVisibilityState();

    this.animate.set(true);
    const animateTimer = setTimeout(() => {
      this.animate.set(false);
    }, 260); // animation completes in 200ms

    const reflowTimer = setTimeout(() => {
      // Reflow anything that's snapped to this
      // note: there's two nav side refs (overlay vs normal)
      // so we check both since we're swapping between them
      const browserSize = document.body.getBoundingClientRect();
      for (const navSideRef of this.navSideRefs()) {
        this.windowManagerService.tryHandleSnapElementPositionChange(
          navSideRef.nativeElement,
          browserSize,
        );
      }
      // Check when animation completes
    }, 230);

    // Cleanup if interrupted
    cleanup(() => {
      this.animate.set(false);
      clearTimeout(animateTimer);
      clearTimeout(reflowTimer);
    });
  });

  @ContentChild("register", { static: false, descendants: false })
  registerTemplate: TemplateRef<{
    registerNavTemplates: (maps: NavTemplatesMap) => void;
  }> | null = null;

  @ContentChild("navTopLogo", { static: false, descendants: false })
  navTopLogoTemplate: TemplateRef<NavTopLogoContext> | null = null;

  @ContentChild("navTopMiddleLogo", { static: false, descendants: false })
  navTopMiddleLogoTemplate: TemplateRef<NavTopLogoContext> | null = null;

  @ContentChild("navTopStartActions", { static: false, descendants: false })
  navTopStartActionsTemplate: TemplateRef<NavTopActionsContext> | null = null;

  @ContentChild("navTopEndActions", { static: false, descendants: false })
  navTopEndActionsTemplate: TemplateRef<NavTopActionsContext> | null = null;

  readonly hasNavSide = signal<boolean | undefined>(undefined);
  readonly navSideMinimized = signal<boolean | undefined>(undefined);

  @ContentChild("navSideContent", { static: false, descendants: false })
  navSideContentTemplate: TemplateRef<NavSideActionsContext> | null = null;

  @ContentChild("navSideHeader", { static: false, descendants: false })
  navSideHeaderTemplate: TemplateRef<NavSideLogoContext> | null = null;

  @ContentChild("navSidePreStartActions", { static: false, descendants: false })
  navSidePreStartActionsTemplate: TemplateRef<NavSideActionsContext> | null =
    null;

  @ContentChild("navSideStartActions", { static: false, descendants: false })
  navSideStartActionsTemplate: TemplateRef<NavSideActionsContext> | null = null;

  @ContentChild("navSideEndActions", { static: false, descendants: false })
  navSideEndActionsTemplate: TemplateRef<NavSideActionsContext> | null = null;

  @ContentChild("navSideEndSmallishOnlyActions", {
    static: false,
    descendants: false,
  })
  navSideEndSmallishOnlyActionsTemplate: TemplateRef<NavSideActionsContext> | null =
    null;

  @ContentChild("navSideStartLargishOnlyActions", {
    static: false,
    descendants: false,
  })
  navSideStartLargishOnlyActionsTemplate: TemplateRef<NavSideActionsContext> | null =
    null;

  @ContentChild("navBottom", { static: false, descendants: false })
  navBottomTemplate: TemplateRef<never> | null = null;

  readonly navSideRefs = viewChildren<StandardNavSideComponent>("navSide");
  readonly navSideRef = computed(() => {
    const navSideRefs = this.navSideRefs();
    const navSideOverlayed = this.navSideOverlayed();

    if (navSideRefs.length === 0) {
      return undefined;
    } else if (navSideRefs.length === 1) {
      return navSideRefs[0];
    } else if (navSideRefs.length === 2) {
      const first = navSideRefs[0]!;
      const second = navSideRefs[1]!;

      if (navSideOverlayed) {
        return second;
      }

      return first;
    }

    // Too many navSideRefs
    throw new Error();
  });

  @ViewChild("contentScroller") contentScroller:
    | ElementRef<HTMLElement>
    | undefined;

  protected readonly transitionState = signal<"idle" | "loading">("loading");

  private observer: ResizeObserver | undefined;
  private isUpdating: boolean = false;
  protected readonly widthCategory = signal<WidthCategory | undefined>(
    undefined,
  );

  private lastWidth: number | undefined;

  ngOnInit() {
    const preferredNavSideExpandedKey = this.preferredNavSideExpandedKey();
    if (preferredNavSideExpandedKey) {
      const readValue = localStorage.getItem(preferredNavSideExpandedKey);
      const newValue = convertTo(readValue, "boolean");
      if (newValue === false) {
        this.onToggleNavSide(false);
      }
    }

    this.lastWidth = this.host.nativeElement.clientWidth;
    this.updateWidthCategoryTrigger$.next();

    this.observer = new ResizeObserver(() => {
      const newWidth = this.host.nativeElement.clientWidth;
      if (this.lastWidth === newWidth) {
        return;
      }

      this.lastWidth = newWidth;
      this.updateWidthCategoryTrigger$.next();
    });

    this.observer.observe(this.host.nativeElement);
  }

  ngOnDestroy() {
    this.observer?.disconnect();
  }

  resetScroll(scrollBehavior: ScrollBehavior) {
    // automatically scroll to top
    this.contentScroller?.nativeElement.scrollTo(0, 0);

    if (this.scrollActiveNavItemIntoView) {
      setTimeout(() => {
        // automatically scroll active side nav item into view
        const navSideRef = this.navSideRef();
        if (navSideRef) {
          navSideRef.scrollActiveNavItemIntoView(scrollBehavior);
        }
      });
    }
  }

  updateWidthCategory() {
    if (this.isUpdating) {
      return;
    }

    if (this.host.nativeElement.clientWidth === 0) {
      // bad data, skip
      return;
    }

    this.isUpdating = true;

    const newWidthCategory = getWidthCategory(
      this.host.nativeElement.clientWidth,
    );
    this.resetShellExpansions(newWidthCategory);

    const wasLoading = this.transitionState() === "loading";
    this.transitionState.set("idle");

    requestAnimationFrame(() => {
      this.isUpdating = false;

      if (wasLoading) {
        this.resetScroll("instant");
      }
    });
  }

  private resetShellExpansions(newCategory: WidthCategory) {
    const oldCategory = this.widthCategory();

    let preferredNavSideExpanded: boolean | undefined;
    const preferredNavSideExpandedKey = this.preferredNavSideExpandedKey();
    if (preferredNavSideExpandedKey) {
      preferredNavSideExpanded = this.preferredNavSideExpanded();
    }

    if (preferredNavSideExpanded === false) {
      this.navSideOverlayed.set(false);
      this.toggleNavSide(false);
      this.widthCategory.set(newCategory);
    } else if (
      this.widthCategory() === undefined ||
      newCategory !== oldCategory
    ) {
      this.navSideOverlayed.set(
        this.sideOverlayFullMinSize <= newCategory &&
          newCategory <= this.sideOverlayFullMaxSize,
      );

      // Default to hidden or shown
      if (oldCategory === undefined) {
        if (
          this.defaultSideCollapsedMinSize <= newCategory &&
          newCategory <= this.defaultSideCollapsedMaxSize
        ) {
          this.toggleNavSide(false);
        } else {
          this.toggleNavSide(true);
        }
      } else {
        // When changing to small, auto-hide
        if (
          oldCategory > this.autoHideSideNavCutoffMaxSize &&
          newCategory <= this.autoHideSideNavCutoffMaxSize
        ) {
          this.toggleNavSide(false);
        }
        // When changing to large, auto-show
        else if (
          this.autoShow &&
          oldCategory < this.autoShowSideNavCutoffMinSize &&
          newCategory >= this.autoShowSideNavCutoffMinSize &&
          newCategory <= this.autoShowSideNavCutoffMaxSize
        ) {
          this.toggleNavSide(true);
        }
      }

      this.widthCategory.set(newCategory);
    }

    newCategory = this.widthCategory()!;
    let hasNavSide: boolean | undefined;
    if (newCategory <= this.topStartToSideStartMaxSize) {
      hasNavSide = true;
    } else if (newCategory <= this.topEndToSideEndMaxSize) {
      hasNavSide = true;
    } else if (this.navSideStartActionsTemplate) {
      hasNavSide = true;
    } else if (this.navSideContentTemplate) {
      hasNavSide = true;
    } else if (this.navSideEndActionsTemplate) {
      hasNavSide = true;
    } else if (
      newCategory > MD &&
      this.navSideStartLargishOnlyActionsTemplate
    ) {
      hasNavSide = true;
    } else {
      hasNavSide = false;
    }

    this.hasNavSide.set(hasNavSide);
  }

  public onToggleNavSide(newExpanded?: boolean) {
    newExpanded = this.toggleNavSide(newExpanded);

    const preferredNavSideExpandedKey = this.preferredNavSideExpandedKey();
    if (preferredNavSideExpandedKey) {
      localStorage.setItem(
        preferredNavSideExpandedKey,
        newExpanded ? "true" : "false",
      );
      this.preferredNavSideExpanded.set(newExpanded);

      if (!newExpanded) {
        this.navSideExpanded.set(false);
      }
    }

    // Focus first element in side nav when it's expanding
    if (newExpanded) {
      setTimeout(() => {
        const navSideRef = this.navSideRef();
        if (navSideRef) {
          const success = navSideRef.focusFirst();
        }
      }, 100);
    }
  }

  private toggleNavSide(newExpanded?: boolean) {
    newExpanded ??= !this.navSideExpanded();

    if (this.navSideExpanded() !== newExpanded) {
      this.navSideExpanded.set(newExpanded);
    }

    // Show/hide scrollbar
    if (this.navSideExpanded() && this.navSideOverlayed()) {
      const scrollbarWidth = this.scrollbarWidthService.scrollbarWidth()!;

      document.body.style.overflowY = "hidden";
      document.body.style.paddingInlineEnd = scrollbarWidth;
    } else {
      document.body.style.overflowY = "auto";
      document.body.style.paddingInlineEnd = "0px";
    }

    return newExpanded;
  }

  public registerNavTemplates(maps: NavTemplatesMap) {
    for (const [key, val] of Object.entries(maps)) {
      // eslint-disable-next-line
      (this as any)[key] = val ?? undefined;
    }

    const widthCategory = this.widthCategory();
    invariant(widthCategory);
    this.resetShellExpansions(widthCategory);

    // Refresh
    this.changeDetectorRef.markForCheck();
  }

  #respondToNavSideCollapserStart = effect(() => {
    const navSideVisibilityState = this.navSideVisibilityState();

    switch (navSideVisibilityState) {
      case "minimized":
        this.navSideMinimized.set(true);
        break;

      default:
        // will change on done
        break;
    }
  });
  #respondToNavSideCollapserDone = effect((cleanup) => {
    const navSideVisibilityState = this.navSideVisibilityState();

    const timer = setTimeout(() => {
      switch (navSideVisibilityState) {
        case "minimized":
          // changed on start
          break;

        default:
          this.navSideMinimized.set(false);
          break;
      }
    }, 200); // animation completes in 200ms

    // Cleanup if interrupted
    cleanup(() => {
      clearTimeout(timer);
    });
  });
}
